// React
import React, { useState } from 'react';

// Store
import useSiteStore from '../../../store/siteStore.js';
import useCoachStore from '../../../store/coachStore.js';

// Hooks
import { useAppTheme } from '../../../hooks/useAppTheme.js';

// Components
import {
  Box,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Tooltip,
} from '@mui/material';
import {
  ClearOutlined,
  PersonOutline,
  SortOutlined,
} from '@mui/icons-material';

import AthleteList from '../../custom/AthleteList.js';
import ParentList from '../../custom/ParentList.js';
import CreateWeeklyScheduleSettings from '../../custom/CreateWeeklyScheduleSettings.js';
import CreateWeeklyScheduleAthletes from '../../custom/CreateWeeklyScheduleAthletes.js';
import CreateWeeklyScheduleSessions from '../../custom/CreateWeeklyScheduleSessions.js';
import WeeklyScheduleTable from '../../custom/WeeklyScheduleTable.js';
import WeeklyScheduleHeader from '../../custom/WeeklyScheduleHeader.js';
import CustomTextField from '../../custom/TextField.js';
import YearCalendar from '../../custom/YearCalendar.js';
import CustomSnackbar from '../../custom/CustomSnackbar.js';

// Style
import { blue } from '@mui/material/colors/index.js';
import styles from './coachHome.module.css';
import { darkTheme } from '../../../styles/colorSchemes/index.js';

// Other
import { deleteWeeklySchedule as deleteWS } from '../../../service/httpService/weeklySchedule/index.js';

const CoachHome = (props) => {
  // Props vom Parent
  const {
    addTrainingSession,
    athletes = [],
    checkedAthletes,
    handleChangeTrainingSession,
    handlePutTextblock,
    handleSelectAthlete,
    handleSend,
    handleSendTextblock,
    handleToggleAthlete,
    onChangeSelectedViewAthletesTab,
    onChangeStartWeek,
    openAndCloseTableRow,
    openAndCloseAll,
    parents = [],
    prepCreateWeeklySchedule,
    goToWeeklySchedule,
    repetitions,
    selectedAthlete,
    selectedViewAthletesTab,
    setTextblocks,
    startWeek,
    textblocks,
    windowSize,
    onRefreshUserLists,
  } = props;

  const {
    calendarWeekString,
    changeCalenderWeek,
    failMessage,
    highlightCurrentCalenderWeek,
    renderCalenderWeek,
    selectedCalenderWeek,
    selectedTab,
    setSelectedCalenderWeek,
    setSnackbarOpenFail,
    setSnackbarOpenSuccess,
    snackbarOpenFail,
    snackbarOpenSuccess,
    successMessage,
    year,
    setYear,
    weeklySchedule,
    removeWeeklySchedule,
    setSuccessMessage,
    setFailMessage,
    selectedSortingCriteria,
    setSelectedSortingCriteria,
  } = useSiteStore();

  const { athleteSearch, setAthleteSearch, parentSearch, setParentSearch } =
    useCoachStore();

  // Use centralized theme
  const { isDarkMode } = useAppTheme();

  // Sortier-Menü
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const sortMenuOpen = Boolean(sortAnchorEl);

  const sortOptions = [
    { value: 'NachnameAuf', label: 'Nachname – aufsteigend' },
    { value: 'NachnameAb', label: 'Nachname – absteigend' },
    { value: 'GeburtsdatumAuf', label: 'Alter – aufsteigend' },
    { value: 'GeburtsdatumAb', label: 'Alter – absteigend' },
    { value: 'AmpelAuf', label: 'Verfügbare Trainingspläne (Ampel) – aufsteigend' },
    { value: 'AmpelAb', label: 'Verfügbare Trainingspläne (Ampel) – absteigend' },
    { value: 'RueckmeldedatumAuf', label: 'Letzte Rückmeldung – aufsteigend' },
    { value: 'RueckmeldedatumAb', label: 'Letzte Rückmeldung – absteigend' },
  ];

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchorEl(null);
  };

  const handleSortSelect = (value) => {
    setSelectedSortingCriteria(value);
    handleSortClose();
  };

  const handleDeleteWeeklySchedule = () => {
    if (!selectedAthlete || !weeklySchedule) return;

    deleteWS(selectedAthlete.profile.email, year, weeklySchedule.calendarWeek)
      .then(() => {
        removeWeeklySchedule(weeklySchedule.calendarWeek, year);
        onRefreshUserLists(); // Refresh athlete statuses after deleting schedule
        setSuccessMessage('Wochenplan erfolgreich gelöscht!');
        setSnackbarOpenSuccess(true);
      })
      .catch(() => {
        setFailMessage('Löschen des Wochenplans fehlgeschlagen!');
        setSnackbarOpenFail(true);
      });
  };

  return (
    <Box
      sx={{
        justifyContent: 'center',
        ml: '10px',
        mr: '10px',
        alignItems: 'center',
        minWidth: '0px',
        width: '1fr',
      }}
    >
      {/* Snackbar Fehler */}
      <CustomSnackbar
        open={snackbarOpenFail}
        onClose={() => setSnackbarOpenFail(false)}
        message={failMessage}
        severity="error"
        autoHideDuration={4000}
      />

      {/* Snackbar Erfolg */}
      <CustomSnackbar
        open={snackbarOpenSuccess}
        onClose={() => setSnackbarOpenSuccess(false)}
        message={successMessage}
        severity="success"
        autoHideDuration={4000}
      />



      <Box sx={{ width: '100%' }}>
        {/* TAB 0 – ATHLETEN */}
        {selectedTab === 0 && (
          <Box className={styles.athletes}>
            {/* Linke Seite: Athletenliste + Suche + Sortierung */}
            <Paper
              elevation={3}
              sx={{
                height:
                  windowSize.innerHeight > 600
                    ? windowSize.innerHeight - 130
                    : 740,
                overflow: 'auto',
                borderRadius: 3,
                boxShadow: isDarkMode ? '0 4px 16px rgba(0, 0, 0, 0.4)' : '0 4px 16px rgba(0, 0, 0, 0.1)',
                bgcolor: isDarkMode ? darkTheme.bg : undefined,
                transition: 'background-color 0.3s ease, box-shadow 0.3s ease',
              }}
            >
              {/* Suche + Sortierung */}
              <Box sx={{
                p: 2,
                borderBottom: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.08)',
                bgcolor: isDarkMode ? darkTheme.headerBg : 'rgba(0, 0, 0, 0.01)',
                display: 'flex',
                gap: 1.5,
                alignItems: 'center'
              }}>
                <CustomTextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  name="text"
                  autoComplete="off"
                  placeholder={`${athletes?.length} Athleten durchsuchen...`}
                  value={athleteSearch}
                  onChange={(e) => setAthleteSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: athleteSearch && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setAthleteSearch('')}
                          sx={{
                            '&:hover': {
                              bgcolor: 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <ClearOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                      '& fieldset': {
                        borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)',
                        transition: 'border-color 0.2s ease',
                      },
                      '&:hover fieldset': {
                        borderColor: isDarkMode ? darkTheme.accent : 'rgba(33, 150, 243, 0.6)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDarkMode ? darkTheme.accent : 'rgba(25, 118, 210, 1)',
                        borderWidth: 2,
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: isDarkMode ? darkTheme.textPrimary : undefined,
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: isDarkMode ? darkTheme.textSecondary : undefined,
                      opacity: 0.5,
                    }
                  }}
                />

                {/* Sortier-Button */}
                <Tooltip title="Liste sortieren" placement="top">
                  <IconButton
                    sx={{
                      borderRadius: 2,
                      bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                      border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : '1px solid rgba(0, 0, 0, 0.12)',
                      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                      color: isDarkMode ? darkTheme.textSecondary : undefined,
                      '&:hover': {
                        bgcolor: isDarkMode ? darkTheme.accent : 'primary.main',
                        color: 'white',
                        borderColor: isDarkMode ? darkTheme.accent : 'primary.main',
                        boxShadow: isDarkMode ? '0 2px 8px rgba(255, 152, 0, 0.3)' : '0 2px 8px rgba(25, 118, 210, 0.3)',
                      },
                    }}
                    onClick={handleSortClick}
                  >
                    <SortOutlined />
                  </IconButton>
                </Tooltip>

                {/* Sortier-Menü */}
                <Menu
                  anchorEl={sortAnchorEl}
                  open={sortMenuOpen}
                  onClose={handleSortClose}
                  PaperProps={{
                    sx: {
                      borderRadius: 2,
                      mt: 0.5,
                      boxShadow: isDarkMode ? '0 4px 20px rgba(0, 0, 0, 0.4)' : '0 4px 20px rgba(0, 0, 0, 0.15)',
                      minWidth: 280,
                      bgcolor: isDarkMode ? darkTheme.cardBg : undefined,
                      border: isDarkMode ? `1px solid ${darkTheme.cardBorder}` : undefined,
                    },
                  }}
                >
                  {sortOptions.map((opt) => (
                    <MenuItem
                      key={opt.value}
                      selected={opt.value === selectedSortingCriteria}
                      onClick={() => handleSortSelect(opt.value)}
                      sx={{
                        py: 1,
                        px: 2,
                        mx: 1,
                        my: 0.5,
                        borderRadius: 2,
                        transition: 'all 0.2s ease',
                        color: isDarkMode ? darkTheme.textPrimary : undefined,
                        '&:hover': {
                          bgcolor: isDarkMode ? darkTheme.cardHover : blue[50],
                        },
                        '&.Mui-selected': {
                          bgcolor: isDarkMode ? `${darkTheme.accent}22` : blue[100],
                          color: isDarkMode ? darkTheme.accent : undefined,
                          '&:hover': {
                            bgcolor: isDarkMode ? `${darkTheme.accent}33` : blue[100],
                          }
                        },
                      }}
                    >
                      {opt.label}
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              <AthleteList
                className={styles.athleteList}
                search={athleteSearch}
                isEditable={false}
                athletes={athletes}
                handleSelectAthlete={handleSelectAthlete}
                isNationalCoach={false}
                onRefreshUserLists={onRefreshUserLists}
              />
            </Paper>

            {/* Rechte Seite: Wochenpläne / Kalender / Erstellen */}
            <Paper
              className={styles.divtable}
              elevation={3}
              sx={{
                height:
                  windowSize.innerHeight > 600
                    ? windowSize.innerHeight - 130
                    : 740,
                overflow: 'hidden',
                borderRadius: 3,
                bgcolor: isDarkMode ? darkTheme.bg : 'background.paper',
                boxShadow: isDarkMode ? '0 4px 16px rgba(0, 0, 0, 0.4)' : '0 4px 16px rgba(0, 0, 0, 0.1)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              {/* Neuer Header mit KW-Navigation, Ansicht, usw. */}
              <WeeklyScheduleHeader
                selectedAthlete={selectedAthlete}

                selectedViewTab={selectedViewAthletesTab}
                onChangeViewTab={onChangeSelectedViewAthletesTab}
                prepCreateWeeklySchedule={prepCreateWeeklySchedule}
                handleSend={handleSend}
                selectedCalenderWeek={selectedCalenderWeek}
                setSelectedCalenderWeek={setSelectedCalenderWeek}
                calendarWeekString={calendarWeekString}
                changeCalenderWeek={changeCalenderWeek}
                renderCalenderWeek={renderCalenderWeek}
                highlightCurrentCalenderWeek={highlightCurrentCalenderWeek}
                year={year}
                setYear={setYear}
                weeklySchedule={weeklySchedule}
                isEditable={true}
                onDeleteWeeklySchedule={handleDeleteWeeklySchedule}
              />

              {/* Jahreskalender */}
              {selectedTab === 0 &&
                selectedViewAthletesTab === 'calendarYear' && (
                  <Box sx={{ flex: 1, overflow: 'hidden' }}>
                    <YearCalendar
                      selectedAthlete={selectedAthlete}
                      currentYear={year}
                      prepCreateWeeklySchedule={prepCreateWeeklySchedule}
                      goToWeeklySchedule={goToWeeklySchedule}
                      onYearChange={setYear}
                    />
                  </Box>
                )}

              {/* Wochenkalender */}
              {selectedTab === 0 &&
                selectedViewAthletesTab === 'calendarWeek' &&
                selectedAthlete !== null && (
                  <Box id="weekly-schedule-table" sx={{ flex: 1, overflow: 'auto' }}>
                    <WeeklyScheduleTable
                      handleChangeTrainingSession={
                        handleChangeTrainingSession
                      }
                      deleteFilesAfterDownload={true}
                      isEditable={true}
                      openAndCloseTableRow={openAndCloseTableRow}
                      openAndCloseAll={openAndCloseAll}
                      selectedAthlete={selectedAthlete}
                    />
                  </Box>
                )}

              {/* Wochenplan erstellen */}
              {selectedViewAthletesTab === 'createWeeklySchedule' && (
                <Box sx={{ flex: 1, overflow: 'hidden' }}>
                  <Paper
                    sx={{
                      overflow: 'auto',
                      height: '100%',
                      borderRadius: 2,
                      boxShadow: isDarkMode ? '0 2px 12px rgba(0, 0, 0, 0.4)' : '0 2px 12px rgba(0, 0, 0, 0.08)',
                      bgcolor: isDarkMode ? darkTheme.bg : undefined,
                      backgroundImage: 'none',
                    }}
                    elevation={3}
                  >
                    <CreateWeeklyScheduleSettings
                      styles={styles}
                      repetitions={repetitions}
                      startWeek={startWeek}
                      onChangeStartWeek={onChangeStartWeek}
                    />
                    <CreateWeeklyScheduleAthletes
                      styles={styles}
                      athletes={athletes}
                      handleToggleAthlete={handleToggleAthlete}
                      checkedAthletes={checkedAthletes}
                      isNationalCoach={false}
                    />
                    <CreateWeeklyScheduleSessions
                      styles={styles}
                      startWeek={startWeek}
                      year={year}
                      isAthlete={false}
                      handleChangeTrainingSession={
                        handleChangeTrainingSession
                      }
                      addTrainingSession={addTrainingSession}
                      textblocks={textblocks}
                      setTextblocks={setTextblocks}
                      handleSendTextblock={handleSendTextblock}
                      handlePutTextblock={handlePutTextblock}
                    />
                  </Paper>
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* TAB 1 – ELTERN */}
        {selectedTab === 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Paper
              elevation={3}
              sx={{
                overflow: 'auto',
                width: '100%',
                height:
                  windowSize.innerHeight > 600
                    ? windowSize.innerHeight - 130
                    : 700,
                borderRadius: 3,
                boxShadow: isDarkMode ? '0 4px 16px rgba(0, 0, 0, 0.4)' : '0 4px 16px rgba(0, 0, 0, 0.1)',
                bgcolor: isDarkMode ? darkTheme.bg : undefined,
              }}
            >
              <Box sx={{
                p: 2,
                borderBottom: isDarkMode ? `0px solid ${darkTheme.cardBorder}` : '0px solid rgba(0, 0, 0, 0.08)',
                bgcolor: isDarkMode ? darkTheme.surface : 'rgba(0, 0, 0, 0.01)',
                display: 'flex',
                gap: 1.5,
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CustomTextField
                  size="small"
                  variant="outlined"
                  name="text"
                  autoComplete="off"
                  placeholder={`${parents?.filter(parent => parent.profile.lastName !== null).length || 0} Eltern durchsuchen...`}
                  value={parentSearch}
                  onChange={(e) => setParentSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutline sx={{ color: isDarkMode ? darkTheme.textSecondary : 'action.active', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                    endAdornment: parentSearch && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => setParentSearch('')}
                          sx={{
                            color: isDarkMode ? darkTheme.textSecondary : undefined,
                            '&:hover': {
                              bgcolor: isDarkMode ? darkTheme.cardHover : 'rgba(0, 0, 0, 0.04)'
                            }
                          }}
                        >
                          <ClearOutlined sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    maxWidth: 600,
                    backgroundColor: isDarkMode ? darkTheme.cardBg : 'white',
                    borderRadius: 2,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: isDarkMode ? darkTheme.cardBg : 'white',
                      '& fieldset': {
                        borderColor: isDarkMode ? darkTheme.cardBorder : 'rgba(0, 0, 0, 0.23)',
                        transition: 'border-color 0.2s ease',
                      },
                      '&:hover fieldset': {
                        borderColor: isDarkMode ? darkTheme.accent : 'rgba(33, 150, 243, 0.6)',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: isDarkMode ? darkTheme.accent : 'rgba(25, 118, 210, 1)',
                        borderWidth: 2,
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: isDarkMode ? darkTheme.textPrimary : 'text.secondary',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: isDarkMode ? darkTheme.textSecondary : 'text.secondary',
                      opacity: 1,
                    }
                  }}
                />
              </Box>

              <ParentList
                sx={{ height: '75vh', overflow: 'auto' }}
                search={parentSearch}
                athletes={athletes}
                isEditable={false}
                parents={parents}
                showAthleteAmount={true}
                onRefreshUserLists={onRefreshUserLists}
              />
            </Paper>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default CoachHome;
